/**
 * <footer-popular> — the footer's "Trending destinations" column, showing the
 * SAME location-aware list as the Popular chip in the search dropdowns.
 *
 * Everything that decides the list lives in esim-search.js (the per-origin
 * country tables, the name normaliser, Shopify's geolocation lookup); this
 * file only imports it, so the footer and the Popular chip can never drift.
 *
 * Markup contract (blocks/footer-popular-destinations.liquid):
 *   <footer-popular data-max="12" data-item-class="menu__item paragraph">
 *     <script type="application/json" class="footer-popular__data">
 *       [{ "name": "Japan", "handle": "japan", "url": "/products/…" }, …]
 *     </script>
 *     <ul class="list-unstyled footer-popular__list"> …server-rendered fallback… </ul>
 *   </footer-popular>
 *
 * The server-rendered rows stay until the list resolves, so the column is never
 * empty — with JS off, or before the geolocation lookup returns, the merchant's
 * fallback collections are what the shopper sees.
 *
 * esim-search.js is imported by the exact URL the theme already loads it from
 * (data-search-module, an asset_url with its ?v= hash) rather than a relative
 * path: a relative import would be a different URL to the browser, evaluate the
 * module a second time, and re-run customElements.define('esim-search').
 */
class FooterPopular extends HTMLElement {
  async connectedCallback() {
    this.list = this.querySelector('.footer-popular__list');
    if (!this.list || !this.dataset.searchModule) return;

    let POPULAR_DEFAULT, POPULAR_BY_COUNTRY, popularKey, detectedCountry;
    try {
      ({ POPULAR_DEFAULT, POPULAR_BY_COUNTRY, popularKey, detectedCountry } = await import(this.dataset.searchModule));
    } catch {
      return; // fallback rows stay
    }
    this.popularKey = popularKey;

    let data = [];
    try {
      data = JSON.parse(this.querySelector('.footer-popular__data')?.textContent || '[]');
    } catch {
      data = [];
    }

    // Destinations by normalised name and handle — the same index the search
    // dropdown builds, so "united states" finds the "USA" collection here too.
    this.byKey = new Map();
    data.forEach((d) => {
      [d.name, d.handle].forEach((v) => {
        const k = popularKey(v);
        if (k && !this.byKey.has(k)) this.byKey.set(k, d);
      });
    });

    this.max = Number(this.dataset.max) || 12;

    const base = this.resolve(POPULAR_DEFAULT);
    if (base.length) this.render(base);

    detectedCountry().then((iso) => {
      const list = POPULAR_BY_COUNTRY[iso];
      if (!list) return;
      const own = this.resolve(list);
      if (!own.length) return;
      // Own destinations lead, the default list tops up the rest — identical to
      // EsimSearch so both surfaces show the same dozen.
      const fill = base.filter((d) => !own.includes(d));
      this.render(own.concat(fill).slice(0, list.length));
    });
  }

  /** List entries → destination objects, in list order; unknown entries drop out. */
  resolve(names) {
    return names.map((n) => this.byKey.get(this.popularKey(n))).filter(Boolean);
  }

  render(items) {
    const itemClass = this.dataset.itemClass || 'menu__item';
    const suffix = this.dataset.suffix || '';
    const frag = document.createDocumentFragment();
    items.slice(0, this.max).forEach((d) => {
      const li = document.createElement('li');
      li.className = itemClass;
      const a = document.createElement('a');
      a.href = d.url;
      a.textContent = suffix ? `${d.name} ${suffix}` : d.name;
      li.appendChild(a);
      frag.appendChild(li);
    });
    this.list.replaceChildren(frag);
  }
}

if (!customElements.get('footer-popular')) {
  customElements.define('footer-popular', FooterPopular);
}
